<template>
  <div class="feature-guide" v-if="isActive">

    <!-- ===================== TOUR MODE OVERLAY ===================== -->
    <div v-if="showOverlay" class="fg-overlay" @click.self="onOverlayClick">
      <svg class="fg-overlay-svg" :width="overlayWidth" :height="overlayHeight">
        <defs>
          <mask :id="'fg-mask-' + featureKey">
            <rect width="100%" height="100%" fill="white" />
            <rect
              :x="spotlightRect.left - 8"
              :y="spotlightRect.top - 8"
              :width="spotlightRect.width + 16"
              :height="spotlightRect.height + 16"
              rx="8" ry="8"
              fill="black"
            />
          </mask>
        </defs>
        <rect
          width="100%" height="100%"
          fill="rgba(0,0,0,0.55)"
          :mask="'url(#fg-mask-' + featureKey + ')'"
        />
      </svg>

      <!-- Spotlight highlight ring -->
      <div class="fg-spotlight-ring" :style="spotlightRingStyle"></div>
    </div>

    <!-- ===================== TOUR POPOVER ===================== -->
    <div v-if="tourActive && currentTargetId" :key="'tour-pop-' + currentStep">
      <b-popover
        :target="currentTargetId"
        :placement="currentPlacement"
        :show="true"
        custom-class="fg-tour-popover"
        triggers=""
        :boundary="panelContainerId"
      >
        <template #title>
          <div class="fg-popover-title">{{ currentStepData.title }}</div>
        </template>
        <div class="fg-popover-body">
          <p class="mb-0">{{ currentStepData.content }}</p>
          <div class="fg-tour-nav">
            <span class="fg-step-counter">{{ currentStep + 1 }} of {{ availableSteps.length }}</span>
            <div class="fg-tour-buttons">
              <button v-if="currentStepIndex > 0"
                class="btn btn-sm btn-outline-secondary mr-1"
                @click="prevStep">Back</button>
              <button
                class="btn btn-sm btn-primary mr-1"
                @click="nextStep">
                {{ isLastStep ? 'Done' : 'Next' }}
              </button>
              <button v-if="!isLastStep"
                class="btn btn-sm btn-link text-muted"
                @click="skipTour">Skip</button>
            </div>
          </div>
        </div>
      </b-popover>
    </div>

    <!-- ===================== HELP DOTS (popovers / helpmode) ===================== -->
    <template v-if="showHelpDots">
      <div v-for="(pos, idx) in dotPositions" :key="'dot-' + idx"
        class="fg-help-dot"
        :class="{
          'fg-dot-pulse': effectiveMode === 'helpmode',
          'fg-dot-static': effectiveMode === 'popovers'
        }"
        :style="pos"
        :id="'fg-dot-' + featureKey + '-' + idx"
        @click.stop="toggleDotPopover(idx)">
        ?
      </div>

      <b-popover
        v-for="(step, idx) in steps" :key="'hpop-' + idx"
        :target="'fg-dot-' + featureKey + '-' + idx"
        :placement="step.placement || 'bottom'"
        :show="activePopoverIndex === idx"
        custom-class="fg-help-popover"
        triggers=""
        boundary="viewport"
      >
        <template #title>{{ step.title }}</template>
        <p class="mb-0">{{ step.content }}</p>
      </b-popover>
    </template>

    <!-- ===================== FIRST-VISIT BANNER (helpmode) ===================== -->
    <div v-if="showFirstVisitBanner" class="fg-help-banner">
      <i class="la la-lightbulb-o mr-2"></i>
      Click the pulsing dots to learn about each feature
      <button class="btn btn-sm btn-link ml-2 text-white" @click="dismissBanner">Dismiss</button>
    </div>

  </div>
</template>

<script>
import _ from 'lodash';

const STORAGE_PREFIX = 'teamio_feature_guide_';

export default {
  name: 'FeatureGuide',

  props: {
    steps: {
      type: Array,
      required: true,
      default: () => [],
    },
    mode: {
      type: String,
      default: 'hybrid',
      validator: val => ['tour', 'popovers', 'helpmode', 'hybrid'].includes(val),
    },
    featureKey: {
      type: String,
      required: true,
    },
    active: {
      type: Boolean,
      default: false,
    },
    panelSelector: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      currentStep: 0,
      activePopoverIndex: -1,
      tourActive: false,
      helpDotsVisible: false,
      isFirstVisit: true,
      showFirstVisitBanner: false,
      spotlightRect: { top: 0, left: 0, width: 0, height: 0 },
      dotPositions: [],
      overlayWidth: '100%',
      overlayHeight: '100%',
      generatedIds: {},
    };
  },

  created() {
    // Non-reactive internal refs — kept out of data() to avoid
    // Vue 2 warnings about underscore-prefixed properties.
    this._panelEl = null;
    this._debouncedReposition = null;
    this._resizeObserver = null;
    this._documentClickHandler = null;
  },

  computed: {
    storageKey() {
      return STORAGE_PREFIX + this.featureKey;
    },

    effectiveMode() {
      if (this.mode === 'hybrid') {
        return this.isFirstVisit ? 'tour' : 'helpmode';
      }
      return this.mode;
    },

    isActive() {
      return this.active && this.steps.length > 0;
    },

    showOverlay() {
      return this.tourActive;
    },

    showHelpDots() {
      return this.helpDotsVisible && !this.tourActive;
    },

    availableSteps() {
      return this.steps.filter((step) => {
        const el = this.getTargetElement(step.target);
        return !!el;
      });
    },

    currentStepData() {
      return this.availableSteps[this.currentStep] || {};
    },

    currentStepIndex() {
      return this.currentStep;
    },

    currentTargetId() {
      if (!this.tourActive || !this.currentStepData.target) return null;
      return this.ensureTargetId(this.currentStepData, this.currentStep);
    },

    currentPlacement() {
      return this.currentStepData.placement || 'bottom';
    },

    isLastStep() {
      return this.currentStep >= this.availableSteps.length - 1;
    },

    panelContainerId() {
      const panelEl = this.getPanelElement();
      if (!panelEl) return null;
      if (!panelEl.id) {
        panelEl.id = 'fg-panel-' + this.featureKey;
      }
      return panelEl.id;
    },

    spotlightRingStyle() {
      const r = this.spotlightRect;
      return {
        position: 'absolute',
        top: (r.top - 8) + 'px',
        left: (r.left - 8) + 'px',
        width: (r.width + 16) + 'px',
        height: (r.height + 16) + 'px',
        borderRadius: '8px',
        border: '2px solid #3abf94',
        boxShadow: '0 0 0 4px rgba(58, 191, 148, 0.3), 0 0 20px rgba(58, 191, 148, 0.2)',
        pointerEvents: 'none',
        transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        zIndex: 10001,
      };
    },
  },

  watch: {
    active(val) {
      if (val) {
        this.$nextTick(() => this.activate());
      } else {
        this.deactivate();
      }
    },

    mode() {
      if (this.active) {
        this.deactivate();
        this.$nextTick(() => this.activate());
      }
    },
  },

  mounted() {
    this.loadVisitState();
    this._debouncedReposition = _.debounce(this.repositionAll, 100);
    this._documentClickHandler = this.onDocumentClick.bind(this);

    if (this.active) {
      this.$nextTick(() => this.activate());
    }
  },

  beforeDestroy() {
    this.teardownListeners();
    this.cleanupGeneratedIds();
  },

  methods: {
    // ==================== localStorage ====================
    loadVisitState() {
      try {
        const saved = localStorage.getItem(this.storageKey);
        if (saved) {
          this.isFirstVisit = false;
          return JSON.parse(saved);
        }
      } catch (e) { /* ignore */ }
      this.isFirstVisit = true;
      return null;
    },

    markVisited() {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify({
          visited: true,
          visitedAt: new Date().toISOString(),
          completedTour: false,
        }));
      } catch (e) { /* ignore */ }
      this.isFirstVisit = false;
    },

    markTourCompleted() {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify({
          visited: true,
          visitedAt: new Date().toISOString(),
          completedTour: true,
        }));
      } catch (e) { /* ignore */ }
    },

    // ==================== Activation ====================
    activate() {
      this.setupListeners();

      switch (this.effectiveMode) {
        case 'tour':
          this.startTour();
          break;
        case 'popovers':
          this.showPopovers();
          break;
        case 'helpmode':
          this.enterHelpMode();
          break;
      }
    },

    deactivate() {
      this.tourActive = false;
      this.helpDotsVisible = false;
      this.activePopoverIndex = -1;
      this.showFirstVisitBanner = false;
      this.currentStep = 0;
      this.teardownListeners();
      this.cleanupGeneratedIds();
    },

    // ==================== Tour Mode ====================
    startTour() {
      this.currentStep = 0;
      this.tourActive = true;
      this.helpDotsVisible = false;
      this.markVisited();
      this.$nextTick(() => {
        this.updateOverlaySize();
        this.scrollTargetIntoView();
        this.$nextTick(() => this.updateSpotlightPosition());
      });
    },

    nextStep() {
      if (this.isLastStep) {
        this.completeTour();
        return;
      }
      this.currentStep++;
      this.$emit('step-change', this.currentStep);
      this.$nextTick(() => {
        this.scrollTargetIntoView();
        this.$nextTick(() => this.updateSpotlightPosition());
      });
    },

    prevStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
        this.$emit('step-change', this.currentStep);
        this.$nextTick(() => {
          this.scrollTargetIntoView();
          this.$nextTick(() => this.updateSpotlightPosition());
        });
      }
    },

    skipTour() {
      this.tourActive = false;
      this.markVisited();
      this.$emit('close');
    },

    completeTour() {
      this.tourActive = false;
      this.markTourCompleted();
      this.$emit('complete');
    },

    onOverlayClick() {
      // Clicking the dark overlay area skips the tour
      this.skipTour();
    },

    // ==================== Popovers Mode ====================
    showPopovers() {
      this.helpDotsVisible = true;
      this.tourActive = false;
      this.$nextTick(() => this.computeDotPositions());
    },

    // ==================== Help Mode ====================
    enterHelpMode() {
      this.helpDotsVisible = true;
      this.tourActive = false;
      if (this.isFirstVisit) {
        this.showFirstVisitBanner = true;
        this.markVisited();
      }
      this.$nextTick(() => this.computeDotPositions());
    },

    dismissBanner() {
      this.showFirstVisitBanner = false;
    },

    // ==================== Dot Popovers ====================
    toggleDotPopover(idx) {
      if (this.activePopoverIndex === idx) {
        this.activePopoverIndex = -1;
      } else {
        this.activePopoverIndex = idx;
      }
    },

    onDocumentClick(e) {
      if (e.target.closest('.fg-help-dot')) return;
      if (e.target.closest('.fg-help-popover')) return;
      if (e.target.closest('.fg-tour-popover')) return;
      this.activePopoverIndex = -1;
    },

    // ==================== Positioning ====================
    getPanelElement() {
      if (this._panelEl) return this._panelEl;
      if (this.panelSelector) {
        this._panelEl = document.querySelector(this.panelSelector);
      }
      if (!this._panelEl && this.$el) {
        this._panelEl = this.$el.closest('.productionTrends') ||
                         this.$el.closest('.slideout-panel') ||
                         (this.$el.parentElement);
      }
      return this._panelEl;
    },

    getTargetElement(target) {
      if (!target) return null;
      if (target.startsWith('#') || target.startsWith('.') || target.startsWith('[')) {
        const panelEl = this.getPanelElement();
        return (panelEl && panelEl.querySelector(target)) || document.querySelector(target);
      }
      if (this.$parent && this.$parent.$refs && this.$parent.$refs[target]) {
        const ref = this.$parent.$refs[target];
        return ref.$el || ref;
      }
      return null;
    },

    ensureTargetId(step, idx) {
      const el = this.getTargetElement(step.target);
      if (!el) return null;
      if (!el.id) {
        const generatedId = 'fg-target-' + this.featureKey + '-' + idx;
        el.id = generatedId;
        this.generatedIds[idx] = generatedId;
      }
      return el.id;
    },

    cleanupGeneratedIds() {
      Object.values(this.generatedIds).forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.removeAttribute('id');
      });
      this.generatedIds = {};
    },

    updateOverlaySize() {
      const panelEl = this.getPanelElement();
      if (!panelEl) return;
      this.overlayWidth = panelEl.scrollWidth + 'px';
      this.overlayHeight = panelEl.scrollHeight + 'px';
    },

    updateSpotlightPosition() {
      const step = this.availableSteps[this.currentStep];
      if (!step) return;

      const targetEl = this.getTargetElement(step.target);
      if (!targetEl) return;

      const panelEl = this.getPanelElement();
      if (!panelEl) return;

      const panelRect = panelEl.getBoundingClientRect();
      const targetRect = targetEl.getBoundingClientRect();

      this.spotlightRect = {
        top: targetRect.top - panelRect.top + panelEl.scrollTop,
        left: targetRect.left - panelRect.left + panelEl.scrollLeft,
        width: targetRect.width,
        height: targetRect.height,
      };
    },

    computeDotPositions() {
      const panelEl = this.getPanelElement();
      if (!panelEl) return;
      const panelRect = panelEl.getBoundingClientRect();

      this.dotPositions = this.steps.map((step) => {
        const targetEl = this.getTargetElement(step.target);
        if (!targetEl) {
          return { display: 'none' };
        }
        const targetRect = targetEl.getBoundingClientRect();
        return {
          position: 'absolute',
          top: (targetRect.top - panelRect.top + panelEl.scrollTop - 8) + 'px',
          left: (targetRect.right - panelRect.left + panelEl.scrollLeft - 8) + 'px',
          zIndex: 10002,
        };
      });
    },

    scrollTargetIntoView() {
      const step = this.availableSteps[this.currentStep];
      if (!step) return;
      const targetEl = this.getTargetElement(step.target);
      if (!targetEl) return;

      const panelEl = this.getPanelElement();
      if (!panelEl) return;

      const panelRect = panelEl.getBoundingClientRect();
      const targetRect = targetEl.getBoundingClientRect();

      if (targetRect.top < panelRect.top || targetRect.bottom > panelRect.bottom) {
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => this.updateSpotlightPosition(), 400);
      }
    },

    repositionAll() {
      if (this.tourActive) {
        this.updateOverlaySize();
        this.updateSpotlightPosition();
      }
      if (this.helpDotsVisible) {
        this.computeDotPositions();
      }
    },

    // ==================== Event Listeners ====================
    setupListeners() {
      document.addEventListener('click', this._documentClickHandler, true);

      const panelEl = this.getPanelElement();
      if (panelEl) {
        panelEl.addEventListener('scroll', this._debouncedReposition);
      }
      window.addEventListener('resize', this._debouncedReposition);

      if (window.ResizeObserver && panelEl) {
        this._resizeObserver = new ResizeObserver(this._debouncedReposition);
        this._resizeObserver.observe(panelEl);
      }
    },

    teardownListeners() {
      document.removeEventListener('click', this._documentClickHandler, true);

      const panelEl = this.getPanelElement();
      if (panelEl) {
        panelEl.removeEventListener('scroll', this._debouncedReposition);
      }
      window.removeEventListener('resize', this._debouncedReposition);

      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.feature-guide {
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  overflow: visible;
  pointer-events: none;
}

// ========================
// OVERLAY (Tour Mode)
// ========================
.fg-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10000;
  pointer-events: auto;
}

.fg-overlay-svg {
  position: absolute;
  top: 0;
  left: 0;
}

.fg-spotlight-ring {
  pointer-events: none;
}

// ========================
// HELP DOTS
// ========================
.fg-help-dot {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background-color: #3abf94;
  color: white;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: auto;
  transition: transform 0.2s ease;
  box-shadow: 0 2px 8px rgba(58, 191, 148, 0.4);
  user-select: none;

  &:hover {
    transform: scale(1.15);
    box-shadow: 0 3px 12px rgba(58, 191, 148, 0.5);
  }
}

.fg-dot-pulse {
  animation: fg-pulse 2s ease-in-out infinite;
}

.fg-dot-static {
  animation: none;
}

@keyframes fg-pulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(58, 191, 148, 0.5);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(58, 191, 148, 0);
  }
}

// ========================
// FIRST-VISIT BANNER
// ========================
.fg-help-banner {
  position: sticky;
  top: 0;
  z-index: 10003;
  background: linear-gradient(135deg, #3abf94 0%, #2d9a78 100%);
  color: white;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  pointer-events: auto;
  animation: fg-banner-slide 0.3s ease-out;
}

@keyframes fg-banner-slide {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>

<!-- Unscoped styles for b-popover custom classes (rendered outside scoped boundary) -->
<style lang="scss">
.fg-tour-popover {
  max-width: 360px;
  z-index: 10005 !important;

  .popover-header {
    background: linear-gradient(135deg, #3abf94 0%, #2d9a78 100%);
    color: white;
    font-weight: 600;
    font-size: 14px;
    border-bottom: none;
    padding: 12px 16px;
  }

  .popover-body {
    padding: 16px;
    font-size: 13px;
    line-height: 1.6;
    color: #334155;
  }

  .fg-tour-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 14px;
    padding-top: 12px;
    border-top: 1px solid #e2e8f0;
  }

  .fg-step-counter {
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
  }

  .fg-tour-buttons {
    display: flex;
    align-items: center;
  }
}

.fg-help-popover {
  max-width: 340px;
  min-width: 280px;
  z-index: 10005 !important;
  border: none !important;
  border-radius: 14px !important;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2), 0 2px 8px rgba(0, 0, 0, 0.1);
  background: #5b8ff9 !important;
  font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, sans-serif;

  .arrow::after {
    border-bottom-color: #5b8ff9 !important;
    border-top-color: #5b8ff9 !important;
    border-left-color: #5b8ff9 !important;
    border-right-color: #5b8ff9 !important;
  }

  .popover-header {
    font-weight: 600;
    font-size: 15px;
    letter-spacing: -0.01em;
    background: #5b8ff9 !important;
    color: white !important;
    border-bottom: none !important;
    padding: 24px 32px 6px 32px !important;
  }

  .popover-body {
    font-size: 13.5px;
    line-height: 1.65;
    color: rgba(255, 255, 255, 0.92) !important;
    background: #5b8ff9 !important;
    padding: 8px 32px 26px 32px !important;
  }
}
</style>
